import { matchPath } from 'react-router-dom';

// Central breadcrumb map for the whole app. Each entry maps a route pattern to
// a trail of crumbs. A crumb with `to` is a link; without it, it's plain
// context (e.g. sidebar section headings, which aren't pages). A crumb with
// `dynamic: true` uses the page-supplied leaf label (see BreadcrumbContext),
// falling back to `label` while that name loads.
//
// The Dashboard home crumb is prepended automatically, so patterns below start
// from the section level.

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const ROUTES = [
  { pattern: '/dashboard', crumbs: () => [] },

  // Horses
  { pattern: '/horse-form', crumbs: () => [{ label: 'Horses' }, { label: 'Horse Search', to: '/horse-form' }] },
  { pattern: '/horse-form/:horseId', crumbs: (p) => [{ label: 'Horses' }, { label: 'Horse Search', to: '/horse-form' }, { label: `Horse #${p.horseId}`, dynamic: true }] },
  { pattern: '/mobile-form', crumbs: () => [{ label: 'Horses' }, { label: 'Mobile Form', to: '/mobile-form' }] },
  { pattern: '/mobile-form/:horseId', crumbs: (p) => [{ label: 'Horses' }, { label: 'Mobile Form', to: '/mobile-form' }, { label: `Horse #${p.horseId}`, dynamic: true }] },
  { pattern: '/horse-compare', crumbs: () => [{ label: 'Horses' }, { label: 'Compare' }] },
  { pattern: '/horse-chart', crumbs: () => [{ label: 'Horses' }, { label: 'Chart' }] },
  { pattern: '/seasonal-records', crumbs: () => [{ label: 'Horses' }, { label: 'Seasonal Records' }] },
  { pattern: '/blackbook', crumbs: () => [{ label: 'Horses' }, { label: 'Blackbook' }] },

  // Breeding
  { pattern: '/horse-stats', crumbs: () => [{ label: 'Breeding' }, { label: 'Horse Stats', to: '/horse-stats' }] },
  { pattern: '/horse-stats/:entity/:entity_id', crumbs: (p) => [{ label: 'Breeding' }, { label: p.entity === 'sire' ? 'Sire Table' : 'Horse Stats', to: p.entity === 'sire' ? '/table/sire' : '/horse-stats' }, { label: `${cap(p.entity)} #${p.entity_id}`, dynamic: true }] },
  { pattern: '/pedigree', crumbs: () => [{ label: 'Breeding' }, { label: 'Pedigree' }] },
  { pattern: '/nicks', crumbs: () => [{ label: 'Breeding' }, { label: 'Nicks' }] },
  { pattern: '/sire-trends', crumbs: () => [{ label: 'Breeding' }, { label: 'Sire Trends' }] },
  { pattern: '/table/:type', crumbs: (p) => [{ label: 'Breeding' }, { label: `${cap(p.type)} Table` }] },
  { pattern: '/sire-crops', crumbs: () => [{ label: 'Breeding' }, { label: 'Crop Table' }] },
  { pattern: '/sire-upcoming', crumbs: () => [{ label: 'Breeding' }, { label: 'Upcoming Starters' }] },
  { pattern: '/sires', crumbs: () => [{ label: 'Breeding' }, { label: 'Sire Dashboard', to: '/sires' }] },
  { pattern: '/sires/:sireId', crumbs: (p) => [{ label: 'Breeding' }, { label: 'Sire Dashboard', to: '/sires' }, { label: `Sire #${p.sireId}`, dynamic: true }] },

  // Racing
  { pattern: '/racing', crumbs: () => [{ label: 'Racing' }, { label: 'Jockey & Trainer Form' }] },
  { pattern: '/racing/race-history', crumbs: () => [{ label: 'Racing' }, { label: 'Race History' }] },
  { pattern: '/racing/upcoming', crumbs: () => [{ label: 'Racing' }, { label: 'Upcoming Runners' }] },
  { pattern: '/racing/ohr', crumbs: () => [{ label: 'Racing' }, { label: 'OHR Predictor' }] },

  // Meetings
  { pattern: '/meetings/ratings', crumbs: () => [{ label: 'Meetings' }, { label: 'Ratings' }] },
  { pattern: '/meetings/:meetingId', crumbs: (p) => [{ label: 'Meetings' }, { label: 'Meeting Form' }, { label: `Meeting ${p.meetingId}`, dynamic: true }] },

  // Sales
  { pattern: '/sales', crumbs: () => [{ label: 'Sales' }, { label: 'Sales List' }] },

  // Data
  { pattern: '/data', crumbs: () => [{ label: 'Data' }] },
  { pattern: '/data/:area', crumbs: (p) => [{ label: `${cap(p.area)} Data` }] },

  // Misc / settings
  { pattern: '/invoice/:code', crumbs: (p) => [{ label: 'Invoice' }, { label: p.code, dynamic: true }] },
  { pattern: '/horse-id-map', crumbs: () => [{ label: 'Settings' }, { label: 'Horse ID Map' }] },
  { pattern: '/blackbook-admin', crumbs: () => [{ label: 'Settings' }, { label: 'Blackbook' }] },
];

const HOME = { label: 'Dashboard', to: '/dashboard' };

// Returns an ordered array of crumbs for a pathname, or null if unmapped.
// The final crumb is the current page (rendered as non-link).
export function buildCrumbs(pathname) {
  for (const route of ROUTES) {
    const match = matchPath(route.pattern, pathname);
    if (match) {
      const rest = route.crumbs(match.params || {});
      // On /dashboard, Home IS the page (leaf); elsewhere it's the root link.
      if (rest.length === 0) return [{ label: 'Dashboard' }];
      return [HOME, ...rest];
    }
  }
  return null;
}
